import { Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import {
    Pupil,
    Lesson,
    MinifiedBox,
    Currency,
    SmartSprint,
    CardTemplate,
    Character
} from '@app/definitions/interfaces';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { PupilService } from '@app/student/services/pupil.service';
import { TaskService } from '@app/core/services/task.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { LessonService } from '@app/teacher/services/lesson.service';
import { TemplateService } from '@app/core/services/template.service';
import { BoxLevel } from '@app/definitions/models';
import { HandlerService } from '@app/_services/handler.service';

@Component({
    selector: 'app-smart-sprint',
    templateUrl: './smart-sprint.component.html',
    styleUrls: ['./smart-sprint.component.scss']
})
export class SmartSprintComponent implements OnInit, OnDestroy {
    pupil: Pupil;
    lesson: Lesson;
    boxes: Array<MinifiedBox>;
    selectedBox: MinifiedBox;
    currentCurrency: Currency;
    boxSmartSprints: Array<SmartSprint>;
    cardTemplate: CardTemplate;
    lessonDate: Date;
    boxLevels: Array<BoxLevel>;
    character: Character;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private pupilService: PupilService,
        private lessonService: LessonService,
        private templateService: TemplateService,
        private taskService: TaskService,
        private handlerService: HandlerService
    ) {}

    ngOnInit() {
        this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params: Params) => {
            const studentId = Number(params['pupil_id']);
            const lessonId = Number(params['lesson_id']);

            this.getPupil(studentId);
            this.getLesson(lessonId);
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    private getPupil(studentId: number) {
        this.pupilService.queryPupil(studentId).subscribe(pupil => {
            this.pupil = pupil;
        });
    }

    private getLesson(lessonId: number) {
        this.taskService.getLesson(lessonId).subscribe(resp => {
            if (resp) {
                this.lesson = resp;
            }
            this.getCurrency();
            this.getBoxes(lessonId);
        });
    }

    private getBoxes(lessonId: number) {
        this.lessonService
            .getLessonBoxes(lessonId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(resp => {
                if (resp) {
                    this.boxes = resp['boxes'];

                    this.boxLevels = resp['box_levels'];

                    if (this.boxes.length === 1 && this.boxes[0].smart_sprints.length) {
                        this.selectedBox = this.boxes[0];
                        this.startSprint();
                        return;
                    }
                    if (this.boxes.length > 1) {
                        this.changeSelectedBox(this.boxes[0]);
                    }
                }
            });
    }

    private getCurrency() {
        this.taskService
            .getCurrencyForLesson(this.lesson)
            .pipe(takeUntil(this.destroy$))
            .subscribe(response => {
                if (response.count !== 0) {
                    const currencies: Currency[] = response.results;
                    this.currentCurrency = currencies[0];
                }
            });
    }

    private generatePreview(): void {
        if (this.selectedBox && this.selectedBox.exercises_in_card !== 0 && this.selectedBox.files.length !== 0) {
            this.templateService
                .sendTemplateRequestByFilesID(this.selectedBox.exercises_in_card, this.selectedBox.files)
                .subscribe(resp => {
                    this.handlerService.checkErrors(resp);
                    if (!resp.error) {
                        this.cardTemplate = resp.data;
                    }
                });
        }
    }

    changeSelectedBox(box: MinifiedBox) {
        this.selectedBox = box;
        this.updateSmartSprints(box);
        this.generatePreview();
    }

    updateSmartSprints(box: MinifiedBox) {
        if (box.smart_sprints.length) {
            this.boxSmartSprints = box.smart_sprints;
        }
    }

    startSprint() {
        this.taskService.getRandomBoxCard(this.selectedBox.id, this.pupil.id).subscribe(resp => {
            this.handlerService.checkErrors(resp);
            if (!resp.error) {
                const card = resp.data;
                this.router.navigate(['student/pupil', this.pupil.id, this.lesson.id, card.id]);
            }
        });
    }

    exitPage() {
        this.location.back();
    }
}
